import { Component } from '../Component';
import { ItemRegistry } from '../../data/ItemRegistry';
import { EventBus } from '../../utils/EventBus';

/**
 * Inventory slot containing an item and quantity
 */
export interface InventorySlot {
  itemId: string;
  quantity: number;
}

/**
 * InventoryComponent - Item storage for entities
 * Typically attached to the player entity
 */
export class InventoryComponent extends Component {
  public readonly type = 'inventory';

  /** Inventory slots */
  public slots: InventorySlot[];

  /** Maximum number of slots */
  public maxSlots: number;

  constructor(data?: Partial<InventoryComponent>) {
    super();
    this.maxSlots = data?.maxSlots ?? 12;
    this.slots = data?.slots ?? [];
  }

  /**
   * Add items to inventory
   * @returns true if items were added successfully
   */
  public addItem(itemId: string, quantity: number = 1): boolean {
    const itemDef = ItemRegistry.get(itemId);
    if (!itemDef) {
      console.warn(`ItemRegistry: Unknown item '${itemId}'`);
      return false;
    }

    // Try to stack with existing slot
    if (itemDef.stackable) {
      const existingSlot = this.slots.find((s) => s.itemId === itemId);
      if (existingSlot) {
        const canAdd = Math.min(quantity, itemDef.maxStack - existingSlot.quantity);
        existingSlot.quantity += canAdd;

        if (canAdd < quantity) {
          // Need to add remainder to new slots
          return this.addToNewSlot(itemId, quantity - canAdd, itemDef.maxStack);
        }

        this.emitChanged();
        return true;
      }
    }

    // Add to new slot
    return this.addToNewSlot(itemId, quantity, itemDef.stackable ? itemDef.maxStack : 1);
  }

  /**
   * Add item to a new slot
   */
  private addToNewSlot(itemId: string, quantity: number, maxStack: number): boolean {
    while (quantity > 0 && this.slots.length < this.maxSlots) {
      const addAmount = Math.min(quantity, maxStack);
      this.slots.push({ itemId, quantity: addAmount });
      quantity -= addAmount;
    }

    if (quantity > 0) {
      console.warn('Inventory full, could not add all items');
      this.emitChanged();
      return false;
    }

    this.emitChanged();
    return true;
  }

  /**
   * Remove items from inventory
   * @returns true if items were removed successfully
   */
  public removeItem(itemId: string, quantity: number = 1): boolean {
    const totalCount = this.getItemCount(itemId);
    if (totalCount < quantity) {
      return false;
    }

    let remaining = quantity;
    for (let i = this.slots.length - 1; i >= 0 && remaining > 0; i--) {
      const slot = this.slots[i];
      if (slot.itemId === itemId) {
        const removeAmount = Math.min(remaining, slot.quantity);
        slot.quantity -= removeAmount;
        remaining -= removeAmount;

        if (slot.quantity <= 0) {
          this.slots.splice(i, 1);
        }
      }
    }

    this.emitChanged();
    return true;
  }

  /**
   * Check if inventory contains an item
   */
  public hasItem(itemId: string, quantity: number = 1): boolean {
    return this.getItemCount(itemId) >= quantity;
  }

  /**
   * Get total count of an item
   */
  public getItemCount(itemId: string): number {
    return this.slots
      .filter((s) => s.itemId === itemId)
      .reduce((sum, s) => sum + s.quantity, 0);
  }

  /**
   * Get all unique item IDs in inventory
   */
  public getUniqueItems(): string[] {
    return [...new Set(this.slots.map((s) => s.itemId))];
  }

  /**
   * Check if inventory is full
   */
  public isFull(): boolean {
    return this.slots.length >= this.maxSlots;
  }

  /**
   * Get number of empty slots
   */
  public getEmptySlotCount(): number {
    return this.maxSlots - this.slots.length;
  }

  /**
   * Clear all items from inventory
   */
  public clear(): void {
    this.slots = [];
    this.emitChanged();
  }

  /**
   * Emit inventory changed event
   */
  private emitChanged(): void {
    if (this.entity) {
      EventBus.emitEvent('inventory:changed', { entityId: this.entity.id });
    }
  }
}
